import {useCallback} from 'react';

import {MAX_NUMBER_VALUE} from 'src/constants';

import type {Dispatch, SetStateAction} from 'react';
import type {TempLimitsSettings} from 'src/modules/usage-limits/typedef';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {CheckBoxChangeData} from 'src/typedef';

type TrackFieldChange = (params: {
    parameterName: string,
    section: string,
    viewType: 'New' | 'Existed',
}) => void;

type Props = {
    setTempLimitsSettings: Dispatch<
        SetStateAction<TempLimitsSettings>
    >,
    mcpLimitsDictionary: DictionaryItem[],
    trackFieldChange: TrackFieldChange,
    getTodayString: () => string,
};

export const useTemporaryLimitActions = ({
    setTempLimitsSettings,
    mcpLimitsDictionary,
    trackFieldChange,
    getTodayString,
}: Props) => {
    const changeTemporaryLimit = useCallback(
        (limitId: string, newValue?: number) => {
            const description =
                mcpLimitsDictionary.find(
                    d => d.code === limitId,
                )?.description ?? limitId;
            trackFieldChange({
                parameterName: description,
                section: 'Connector Limits (Temp)',
                viewType: 'Existed',
            });

            const unlimited = newValue === undefined;
            const value = unlimited
                ? MAX_NUMBER_VALUE
                : newValue;

            setTempLimitsSettings(prevState => ({
                ...prevState,
                limits: [
                    ...prevState.limits.filter(
                        l => l.limitId !== limitId,
                    ),
                    {
                        limitId,
                        value,
                        temporaryDate:
                            prevState.expiresOn || '',
                        unlimited,
                    },
                ],
            }));
        },
        [
            setTempLimitsSettings,
            mcpLimitsDictionary,
            trackFieldChange,
        ],
    );

    const clearTemporaryLimit = useCallback(
        (limitId: string) => {
            setTempLimitsSettings(prevState => ({
                ...prevState,
                limits: prevState.limits.filter(
                    l => l.limitId !== limitId,
                ),
            }));
        },
        [setTempLimitsSettings],
    );

    const changeTemporaryLimitMode = useCallback(
        (changeData: CheckBoxChangeData) => {
            setTempLimitsSettings({
                limits: [],
                expiresOn:
                    changeData.event.currentTarget.checked
                        ? getTodayString()
                        : '',
            });
        },
        [getTodayString, setTempLimitsSettings],
    );

    const onExpiresOnChange = useCallback(
        (expiresOn: string) => {
            trackFieldChange({
                parameterName: 'Until',
                section: 'Connector Limits',
                viewType: 'Existed',
            });
            setTempLimitsSettings(prevState => ({
                ...prevState,
                limits: expiresOn
                    ? prevState.limits.map(limit => ({
                            ...limit,
                            temporaryDate: expiresOn,
                        }))
                    : [],
                expiresOn,
            }));
        },
        [trackFieldChange, setTempLimitsSettings],
    );

    return {
        changeTemporaryLimit,
        clearTemporaryLimit,
        changeTemporaryLimitMode,
        onExpiresOnChange,
    };
};
